import {
  AlertTriangle,
  Bot,
  CircleDollarSign,
  ClipboardCheck,
  CreditCard,
  FileCode2,
  GitBranch,
  PackageCheck,
  PlayCircle,
  PlugZap,
  ReceiptText,
  RefreshCw,
  Search,
  ServerCog,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api, queryString } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { Badge, formatDate } from "../../components/Badge";
import { SearchInput, Select } from "../../components/Form";
import { Empty, ErrorState, Loading, Pagination } from "../../components/State";

type MetricValue = string | number | undefined;

interface ProductMetric {
  key?: string;
  label: string;
  value: MetricValue;
  accent?: boolean;
}

interface ProductRegistryItem {
  id: string;
  name: string;
  provider_name?: string;
  provider_type?: string;
  provider_health?: string;
  provider_status?: string;
  namespace?: string;
  release?: string;
  status: string;
  listing_status?: string;
  protocol_version?: string;
  transport?: string;
  tck_status?: string;
  updated_at?: string;
}

interface ProductRuntimeApp {
  id: string;
  app_name: string;
  product_name: string;
  workspace_name: string;
  feishu_tenant_name?: string;
  feishu_status?: string;
  operation_status?: string;
  watch_status?: string;
  approval_pending?: number;
  last_event_at?: string;
}

interface BillingItem {
  id: string;
  workspace_name: string;
  owner_name?: string;
  currency: string;
  balance_amount: number;
  recharge_amount?: number;
  usage_amount?: number;
  gateway_cost_amount?: number;
  provider_payable_amount?: number;
  reconcile_status?: string;
  updated_at?: string;
}

interface AffRiskItem {
  id: string;
  user_name: string;
  inviter_name?: string;
  invitees_count?: number;
  reward_amount?: number;
  currency?: string;
  risk_level?: string;
  rule?: string;
  status?: string;
  last_triggered_at?: string;
}

interface RegistryResponse {
  metrics?: ProductMetric[];
  items: ProductRegistryItem[];
  total: number;
  page: number;
  page_size: number;
}

interface RuntimeResponse {
  metrics?: ProductMetric[];
  items: ProductRuntimeApp[];
  total: number;
  page: number;
  page_size: number;
}

interface BillingResponse {
  metrics?: ProductMetric[];
  items: BillingItem[];
  total: number;
  page: number;
  page_size: number;
}

interface AffRiskResponse {
  metrics?: ProductMetric[];
  items: AffRiskItem[];
  total: number;
  page: number;
  page_size: number;
}

const registryMetrics = [
  { label: "已上架产品", key: "listed", icon: PackageCheck },
  { label: "Provider", key: "providers", icon: PlugZap },
  { label: "待发布版本", key: "pending_releases", icon: FileCode2 },
  { label: "TCK 异常", key: "tck_failed", icon: AlertTriangle, accent: true },
];

const runtimeMetrics = [
  { label: "企业应用", key: "enterprise_apps", icon: Bot },
  { label: "运行任务", key: "operations", icon: PlayCircle },
  { label: "Watch", key: "watches", icon: ServerCog },
  { label: "待审批", key: "approval_pending", icon: ClipboardCheck, accent: true },
];

const billingMetrics = [
  { label: "平台余额", key: "balance_amount", icon: Wallet },
  { label: "本月充值", key: "recharge_amount", icon: CreditCard },
  { label: "网关用量", key: "gateway_cost_amount", icon: CircleDollarSign },
  { label: "Provider 应付", key: "provider_payable_amount", icon: ReceiptText, accent: true },
];

const affMetrics = [
  { label: "邀请用户", key: "invited_users", icon: GitBranch },
  { label: "奖励余额", key: "reward_amount", icon: Wallet },
  { label: "异常账户", key: "risk_accounts", icon: AlertTriangle, accent: true },
  { label: "生效规则", key: "active_rules", icon: ShieldCheck },
];

const registryTabs = [
  { value: "", label: "全部" },
  { value: "listed", label: "已上架" },
  { value: "reviewing", label: "评审中" },
  { value: "suspended", label: "已暂停" },
];

const runtimeTabs = [
  { value: "", label: "全部" },
  { value: "healthy", label: "运行正常" },
  { value: "attention", label: "需要处理" },
  { value: "approval", label: "待审批" },
];

const billingTabs = [
  { value: "", label: "全部" },
  { value: "open", label: "本期未结" },
  { value: "reconciled", label: "已对账" },
  { value: "exception", label: "异常" },
];

const affTabs = [
  { value: "", label: "全部" },
  { value: "normal", label: "正常" },
  { value: "watch", label: "观察" },
  { value: "blocked", label: "已冻结" },
];

function numberValue(value: MetricValue) {
  if (value === undefined || value === null || value === "") return 0;
  return value;
}

function metricValue(metrics: ProductMetric[] | undefined, key: string) {
  const item = metrics?.find((entry) => entry.key === key || entry.label === key || entry.label === keyLabel(key));
  return numberValue(item?.value);
}

function keyLabel(key: string) {
  return key
    .split("_")
    .map((value) => value.charAt(0).toUpperCase() + value.slice(1))
    .join(" ");
}

function money(value?: number, currency = "RMB") {
  if (typeof value !== "number") return "-";
  const symbol = currency === "USD" ? "$" : "¥";
  return `${symbol}${value.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function tone(status?: string) {
  if (!status) return "neutral";
  if (["active", "listed", "healthy", "reconciled", "passed", "normal", "success", "connected", "succeeded"].includes(status)) return "success";
  if (["reviewing", "pending", "open", "watch", "approval", "attention", "running", "queued", "waiting_approval", "paused", "pending_authorization", "degraded", "coming_soon"].includes(status)) return "warning";
  if (["failed", "exception", "blocked", "suspended", "danger", "disconnected", "revoked", "down"].includes(status)) return "danger";
  return "neutral";
}

function statusText(status?: string) {
  const map: Record<string, string> = {
    active: "有效",
    listed: "已上架",
    reviewing: "评审中",
    suspended: "已暂停",
    healthy: "正常",
    attention: "需处理",
    approval: "待审批",
    open: "未结",
    reconciled: "已对账",
    exception: "异常",
    passed: "通过",
    failed: "失败",
    pending: "待处理",
    normal: "正常",
    watch: "观察",
    blocked: "已冻结",
    connected: "已连接",
    disconnected: "未连接",
    pending_authorization: "待授权",
    revoked: "已撤销",
    running: "运行中",
    queued: "排队中",
    waiting_approval: "待审批",
    succeeded: "已完成",
    idle: "暂无任务",
    none: "未配置",
    paused: "已暂停",
    coming_soon: "接入评审中",
    degraded: "降级",
    down: "不可用",
  };
  return status ? map[status] || status : "-";
}

function useAdminParams(defaultPageSize = 20) {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const status = params.get("status") || "";
  const keyword = params.get("keyword") || "";
  const value = (key: string) => params.get(key) || "";
  const patch = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", key === "page" ? value || "1" : "1");
    setParams(next);
  };
  return { page, pageSize: defaultPageSize, status, keyword, patch, value };
}

function MetricGrid({
  metrics,
  config,
}: {
  metrics?: ProductMetric[];
  config: { label: string; key: string; icon: React.ElementType; accent?: boolean }[];
}) {
  return (
    <section className="metric-grid">
      {config.map(({ icon: Icon, ...item }) => (
        <article className={item.accent ? "accent" : ""} key={item.key}>
          <span>
            <Icon size={19} />
          </span>
          <div>
            <small>{item.label}</small>
            <strong>{metricValue(metrics, item.key)}</strong>
          </div>
        </article>
      ))}
    </section>
  );
}

function AdminTabs({
  value,
  items,
  onChange,
}: {
  value: string;
  items: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="admin-tabs">
      {items.map((item) => (
        <button
          className={value === item.value ? "active" : ""}
          key={item.value || "all"}
          onClick={() => onChange(item.value)}
          type="button"
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function FilterBar({
  keyword,
  status,
  tabs,
  placeholder,
  onPatch,
  children,
}: {
  keyword: string;
  status: string;
  tabs: { value: string; label: string }[];
  placeholder: string;
  onPatch: (key: string, value: string) => void;
  children?: React.ReactNode;
}) {
  return (
    <>
      <AdminTabs value={status} items={tabs} onChange={(value) => onPatch("status", value)} />
      <form
        className="admin-filterbar"
        onSubmit={(event) => {
          event.preventDefault();
          onPatch("keyword", keyword);
        }}
      >
        <label>
          <Search size={16} />
          <SearchInput
            value={keyword}
            onChange={(event) => onPatch("keyword", event.target.value)}
            placeholder={placeholder}
          />
        </label>
        {children}
        <button className="primary-button">查询</button>
      </form>
    </>
  );
}

export function AdminProductRegistryPage() {
  const { page, pageSize, status, keyword, patch, value } = useAdminParams();
  const transport = value("transport");
  const result = useAsyncRegistry(page, pageSize, status, keyword, { transport });
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>PRODUCT GOVERNANCE</span>
          <h1>产品接入治理</h1>
          <p>统一管理 Product Registry、Provider、发布版本和 TCK 准入状态。</p>
        </div>
        <button className="secondary-button" type="button" onClick={result.retry}>
          <RefreshCw size={15} />
          刷新
        </button>
      </header>
      <MetricGrid metrics={result.data?.metrics} config={registryMetrics} />
      <FilterBar
        keyword={keyword}
        status={status}
        tabs={registryTabs}
        placeholder="产品、Provider 或命名空间"
        onPatch={patch}
      >
        <Select value={transport} onChange={(event) => patch("transport", event.target.value)}>
          <option value="">全部协议</option>
          <option value="https-json">HTTPS JSON</option>
          <option value="grpc">gRPC Adapter</option>
          <option value="sidecar">Sidecar</option>
        </Select>
      </FilterBar>
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? (
        <Empty title="暂无产品接入记录" detail="通过受控 Product Registry 上架第一个 Headless Provider。" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>产品</th>
                <th>Provider</th>
                <th>发布版本</th>
                <th>协议</th>
                <th>TCK</th>
                <th>状态</th>
                <th>更新时间</th>
              </tr>
            </thead>
            <tbody>
              {result.data.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.name}</strong>
                    <small>{item.namespace || item.id}</small>
                  </td>
                  <td>
                    <strong>{item.provider_name || "-"}</strong>
                    <small>{item.provider_type || "first-party"}</small>
                    <Badge tone={tone(item.provider_health)}>{statusText(item.provider_health)}</Badge>
                  </td>
                  <td>
                    <strong>{item.release || "-"}</strong>
                    <small>{item.protocol_version || "opc.provider.v1"}</small>
                  </td>
                  <td>{item.transport || "-"}</td>
                  <td>
                    <Badge tone={tone(item.tck_status)}>{statusText(item.tck_status)}</Badge>
                  </td>
                  <td>
                    <div className="tag-row">
                      <Badge tone={tone(item.status)}>{statusText(item.status)}</Badge>
                      {item.listing_status && item.listing_status !== item.status && <Badge tone={tone(item.listing_status)}>{statusText(item.listing_status)}</Badge>}
                    </div>
                  </td>
                  <td>{formatDate(item.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} pageSize={result.data?.page_size || pageSize} total={result.data?.total || 0} onChange={(value) => patch("page", String(value))} />
    </div>
  );
}

export function AdminProductRuntimePage() {
  const { page, pageSize, status, keyword, patch, value } = useAdminParams();
  const feishu = value("feishu");
  const result = useAsyncRuntime(page, pageSize, status, keyword, { feishu });
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>PRODUCT RUNTIME</span>
          <h1>企业应用运行</h1>
          <p>查看企业自建应用、Operation、Watch、飞书连接和人工审批状态。</p>
        </div>
        <button className="secondary-button" type="button" onClick={result.retry}>
          <RefreshCw size={15} />
          刷新
        </button>
      </header>
      <MetricGrid metrics={result.data?.metrics} config={runtimeMetrics} />
      <FilterBar
        keyword={keyword}
        status={status}
        tabs={runtimeTabs}
        placeholder="企业、应用、飞书租户或产品"
        onPatch={patch}
      >
        <Select value={feishu} onChange={(event) => patch("feishu", event.target.value)}>
          <option value="">全部飞书状态</option>
          <option value="connected">已连接</option>
          <option value="disconnected">未连接</option>
        </Select>
      </FilterBar>
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? (
        <Empty title="暂无企业应用" detail="用户创建企业工作区并授权飞书后，会在这里形成运行实例。" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>企业应用</th>
                <th>企业工作区</th>
                <th>飞书企业</th>
                <th>Operation</th>
                <th>Watch</th>
                <th>审批</th>
                <th>最近事件</th>
              </tr>
            </thead>
            <tbody>
              {result.data.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.app_name}</strong>
                    <small>{item.product_name}</small>
                  </td>
                  <td>{item.workspace_name}</td>
                  <td>
                    <Badge tone={tone(item.feishu_status)}>{statusText(item.feishu_status)}</Badge>
                    <small>{item.feishu_tenant_name || "-"}</small>
                  </td>
                  <td>
                    <Badge tone={tone(item.operation_status)}>{statusText(item.operation_status)}</Badge>
                  </td>
                  <td>
                    <Badge tone={tone(item.watch_status)}>{statusText(item.watch_status)}</Badge>
                  </td>
                  <td>{item.approval_pending ? `${item.approval_pending} 项待审批` : "无待办"}</td>
                  <td>{formatDate(item.last_event_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} pageSize={result.data?.page_size || pageSize} total={result.data?.total || 0} onChange={(value) => patch("page", String(value))} />
    </div>
  );
}

export function AdminBillingPage() {
  const { page, pageSize, status, keyword, patch, value } = useAdminParams();
  const currency = value("currency");
  const result = useAsyncBilling(page, pageSize, status, keyword, { currency });
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>BILLING LEDGER</span>
          <h1>余额与结算</h1>
          <p>统一核对充值、平台余额、网关用量、Provider 应付和对账状态。</p>
        </div>
      </header>
      <MetricGrid metrics={result.data?.metrics} config={billingMetrics} />
      <FilterBar
        keyword={keyword}
        status={status}
        tabs={billingTabs}
        placeholder="企业、用户、账期或结算编号"
        onPatch={patch}
      >
        <Select value={currency} onChange={(event) => patch("currency", event.target.value)}>
          <option value="">全部币种</option>
          <option value="CNY">人民币 CNY</option>
          <option value="USD">USD</option>
        </Select>
      </FilterBar>
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? (
        <Empty title="暂无结算记录" detail="充值订单、网关用量和 Provider 应付入账后会形成可核对明细。" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>余额账户</th>
                <th>可用余额</th>
                <th>充值</th>
                <th>产品消费</th>
                <th>网关成本</th>
                <th>Provider 应付</th>
                <th>对账</th>
              </tr>
            </thead>
            <tbody>
              {result.data.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.workspace_name}</strong>
                    <small>{item.owner_name || item.id}</small>
                  </td>
                  <td>{money(item.balance_amount, item.currency)}</td>
                  <td>{money(item.recharge_amount, item.currency)}</td>
                  <td>{money(item.usage_amount, item.currency)}</td>
                  <td>{money(item.gateway_cost_amount, item.currency)}</td>
                  <td>{money(item.provider_payable_amount, item.currency)}</td>
                  <td>
                    <Badge tone={tone(item.reconcile_status)}>{statusText(item.reconcile_status)}</Badge>
                    <small>{formatDate(item.updated_at)}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} pageSize={result.data?.page_size || pageSize} total={result.data?.total || 0} onChange={(value) => patch("page", String(value))} />
    </div>
  );
}

export function AdminAffRiskPage() {
  const { page, pageSize, status, keyword, patch, value } = useAdminParams();
  const rule = value("rule");
  const result = useAsyncAffRisk(page, pageSize, status, keyword, { rule });
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>AFF RISK CONTROL</span>
          <h1>邀请奖励风控</h1>
          <p>只治理用户邀请用户、充值触发奖励和异常账户，不进入产品或应用关系。</p>
        </div>
      </header>
      <MetricGrid metrics={result.data?.metrics} config={affMetrics} />
      <FilterBar
        keyword={keyword}
        status={status}
        tabs={affTabs}
        placeholder="用户、邀请人、规则或风险标签"
        onPatch={patch}
      >
        <Select value={rule} onChange={(event) => patch("rule", event.target.value)}>
          <option value="">全部规则</option>
          <option value="recharge_reward">充值奖励</option>
          <option value="same_device">同设备</option>
          <option value="payment_risk">支付异常</option>
        </Select>
      </FilterBar>
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? (
        <Empty title="暂无邀请风控记录" detail="直接邀请人的余额奖励、冻结和撤销都会在这里留痕。" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>用户</th>
                <th>直接邀请人</th>
                <th>邀请数</th>
                <th>奖励余额</th>
                <th>规则</th>
                <th>风险</th>
                <th>状态</th>
              </tr>
            </thead>
            <tbody>
              {result.data.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.user_name}</strong>
                    <small>{item.id}</small>
                  </td>
                  <td>{item.inviter_name || "-"}</td>
                  <td>{item.invitees_count || 0}</td>
                  <td>{money(item.reward_amount, item.currency)}</td>
                  <td>{item.rule || "-"}</td>
                  <td>
                    <Badge tone={tone(item.risk_level)}>{statusText(item.risk_level)}</Badge>
                  </td>
                  <td>
                    <Badge tone={tone(item.status)}>{statusText(item.status)}</Badge>
                    <small>{formatDate(item.last_triggered_at)}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} pageSize={result.data?.page_size || pageSize} total={result.data?.total || 0} onChange={(value) => patch("page", String(value))} />
    </div>
  );
}

function useAsyncRegistry(page: number, pageSize: number, status: string, keyword: string, extra: Record<string, string>) {
  return useProductPage<RegistryResponse>("/admin/product-registry", page, pageSize, status, keyword, extra);
}

function useAsyncRuntime(page: number, pageSize: number, status: string, keyword: string, extra: Record<string, string>) {
  return useProductPage<RuntimeResponse>("/admin/product-runtime", page, pageSize, status, keyword, extra);
}

function useAsyncBilling(page: number, pageSize: number, status: string, keyword: string, extra: Record<string, string>) {
  return useProductPage<BillingResponse>("/admin/billing", page, pageSize, status, keyword, extra);
}

function useAsyncAffRisk(page: number, pageSize: number, status: string, keyword: string, extra: Record<string, string>) {
  return useProductPage<AffRiskResponse>("/admin/aff-risk", page, pageSize, status, keyword, extra);
}

function useProductPage<T extends { page: number; page_size: number; total: number }>(
  path: string,
  page: number,
  pageSize: number,
  status: string,
  keyword: string,
  extra: Record<string, string>,
) {
  return useAsync(
    () =>
      api<T>(
        `${path}${queryString({ page, pageSize, status, keyword, ...extra })}`,
      ),
    [path, page, pageSize, status, keyword, ...Object.values(extra)],
  );
}
